import { useEffect, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useLoaderData } from "react-router";

import { Button } from "@components/Button";
import { Image } from "@components/Image";
import { Slider } from "@components/Slider";
import { Stack } from "@components/Stack";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/Tooltip";
import { Typography } from "@components/Typography";

import { useHover } from "@hooks/useHover";

import { usePrivateRequestDeclineMutation } from "@storage/queries/privateRequest";
import { TSessionData } from "@storage/session/types";
import { userAuthDataStorage } from "@storage/sessionStorage";

import { QUERY_KEYS } from "@configs/api";
import { DELAY_TIME_1000 } from "@configs/constants";
import { ICON_PATHS } from "@configs/icons";

interface PrivateActionsProps {
	isWithClientAudio: boolean;
	mute: (muted: boolean) => void;
	changeVolume: (volume: number) => void;
	id: number;
}

export const PrivateRequestActions = ({ isWithClientAudio, mute, changeVolume, id }: PrivateActionsProps) => {
	const [muted, setMuted] = useState(false);
	const [volume, setVolume] = useState(0);

	const { currentAccount } = useLoaderData<TSessionData>();
	const privateRequestDeclineMutation = usePrivateRequestDeclineMutation();
	const queryClient = useQueryClient();
	const [ref, isHovering] = useHover();

	useEffect(() => {
		setMuted(currentAccount.audio_chat_muted || false);
		setVolume(currentAccount.audio_chat_volume || 0);
	}, []);

	const handleSound = () => {
		setMuted(!muted);
		mute(!muted);
		if (muted === true && volume === 0) {
			changeVolume(0.5);
			setVolume(0.5);

			userAuthDataStorage.setItemAsJson({ ...currentAccount, audio_chat_volume: 0.5 });
		}

		userAuthDataStorage.setItemAsJson({ ...currentAccount, audio_chat_muted: !muted });
	};

	const onChangeVolume = (value: number | readonly number[]) => {
		const rawVolume = typeof value === "number" ? value / 100 : value[0] / 100;

		if (rawVolume < 0) return;

		const isMuted = rawVolume === 0;
		changeVolume(rawVolume);

		setVolume(rawVolume);
		setMuted(isMuted);
		mute(isMuted);

		userAuthDataStorage.setItemAsJson({ ...currentAccount, audio_chat_volume: rawVolume, audio_chat_muted: isMuted });
	};

	const handleStopPrivate = async () => {
		privateRequestDeclineMutation.mutate(id);
		setTimeout(async () => {
			queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BROADCAST_STATUS_DATA] });
		}, DELAY_TIME_1000);
	};

	return (
		<Stack direction="row" spacing={8} align="center">
			{isWithClientAudio ? (
				<Tooltip>
					<TooltipTrigger
						ref={ref}
						render={
							<Stack direction="row" spacing={8} align="center">
								{isHovering ? (
									<div className="h-7 w-26 rounded-[30px] bg-secondary p-3 shadow-[0px_2px_8px_rgba(0,0,0,0.4)]">
										<Slider value={volume * 100} max={100} min={0} onValueChange={onChangeVolume} />
									</div>
								) : null}
								<Button
									className="h-10! w-10! rounded-full! border-none! p-0!"
									onClick={handleSound}
									type="secondary"
									shape="circle"
									icon={
										!muted ? (
											<Image width={24} height={24} src={ICON_PATHS.SOUND_ON} alt="soundOn" />
										) : (
											<Image width={24} height={24} src={ICON_PATHS.SOUND_OFF} alt="soundOff" />
										)
									}
								/>
							</Stack>
						}
					/>
					<TooltipContent sideOffset={12}>
						<Typography type="span" padding="none">
							{muted ? "Unmute" : "Mute"}
						</Typography>
					</TooltipContent>
				</Tooltip>
			) : null}
			<Tooltip>
				<TooltipTrigger render={<span className="inline-block size-10" />}>
					<Button type="red100" size="medium" shape="circle" className="h-10 w-10" onClick={handleStopPrivate}>
						<Image width={14} height={14} src={ICON_PATHS.STOP_PRIVATE} alt="stop" />
					</Button>
				</TooltipTrigger>
				<TooltipContent sideOffset={12}>
					<Typography type="span" padding="none">
						Stop private show
					</Typography>
				</TooltipContent>
			</Tooltip>
		</Stack>
	);
};
